import { useState } from "react";
import { router } from "@inertiajs/react";
import axios, { isAxiosError } from "axios";
import { Location } from "@components/ui/location_input";
import useTranslation from "@hooks/use_translation";

type Paths = {
  workPreferences: string;
  subscriptions: string;
  dashboard: string;
};

export type JobAlert = {
  jobBoardId: number;
  keywords: string[];
};

type Args = {
  paths: Paths;
  desiredJobTitles: string[];
  location?: Location | null;
  jobAlert?: JobAlert | null;
};

export const useOnboardingPreferencesForm = ({
  paths,
  desiredJobTitles,
  location: initialLocation,
  jobAlert,
}: Args) => {
  const { t } = useTranslation("onboarding", { keyPrefix: "submit" });

  const [worksites, setWorksites] = useState<string[]>([]);
  const [location, setLocation] = useState<Location | null>(initialLocation ?? null);
  const [jobTitles, setJobTitles] = useState<string[]>(desiredJobTitles ?? []);
  const [jobTitlesError, setJobTitlesError] = useState<string | null>(null);
  const [createJobAlert, setCreateJobAlert] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const skip = () => router.visit(paths.dashboard);

  const submit = async () => {
    setLoading(true);
    setError(null);
    setJobTitlesError(null);

    try {
      const formData = new FormData();

      formData.append("sections[]", "worksites");
      worksites.forEach((category) => {
        formData.append("worksites[][category]", category);
      });

      formData.append("preference[location]", JSON.stringify(location));
      formData.append("preference[desired_job_titles]", JSON.stringify(jobTitles));

      const requests: Promise<unknown>[] = [axios.patch(paths.workPreferences, formData)];

      if (jobAlert && createJobAlert) {
        requests.push(
          axios.post(paths.subscriptions, {
            job_board_id: jobAlert.jobBoardId,
            keywords: jobAlert.keywords,
          }),
        );
      }

      await Promise.all(requests);

      router.visit(paths.dashboard);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 422) {
        const { errors } = e.response.data;

        if (errors?.desired_job_titles) {
          setJobTitlesError(errors.desired_job_titles[0]);
        }
      }

      console.error(e);
      setError(t("error"));
      setLoading(false);
    }
  };

  return {
    worksites,
    setWorksites,
    location,
    setLocation,
    jobTitles,
    setJobTitles,
    jobTitlesError,
    createJobAlert,
    setCreateJobAlert,
    error,
    loading,
    submit,
    skip,
  };
};
